import styled from '@emotion/styled';
import { CrStatusBanner } from '../projectOverview';
import { routes } from '../../../../routes';
import { Link } from 'react-router-dom';
import { Table } from '@mantine/core';

const CustomLink = styled(Link)`
  color: black;
`;

export const getPlantsObject = (plants) => {
  let plantsObject = {};
  plants.forEach((plant) => {
    if (!plantsObject[plant.id])
      plantsObject[plant.producing_plant_id] = {
        pp_id: plant.producing_plant_id,
        step_id: plant.id,
        status: plant.status,
      };
  });

  return plantsObject;
};

export const getPlantName = (id, plantData) => {
  const plant = plantData.find((plant) => plant.id === id);
  return plant.producing_plant;
};

export const StyledTotal = styled.div`
  color: #4c86dd;
  font-size: 16px;
  font-weight: bold;
  text-transform: uppercase;
`;

const getVariance = (main = 0, ref = 0) => {
  const variance = parseFloat(main) - parseFloat(ref);
  return (variance < 0 ? -variance : variance).toFixed(3);
};

const getFinalValue = (main, ref) => {
  const parseMain = main ?? 'N/A';
  const parsedRef = ref ?? 'N/A';

  const mainCost = parseMain !== 'N/A' ? parseFloat(main).toFixed(3) : 'N/A';
  const refCost = parsedRef !== 'N/A' ? parseFloat(ref).toFixed(3) : 'N/A';
  const variance = mainCost !== 'N/A' && refCost !== 'N/A' ? getVariance(main, ref) : 'N/A';

  return [`Main: ${mainCost}`, `Ref: ${refCost}`, `Var: ${variance}`];
};

const getValueData = (item, reference, fields, type = 'cc', refType) => {
  const result = {};
  const prefix = refType === 'secondary' ? refType : null;
  if (type === 'bom') {
    fields.forEach((field) => {
      const fieldNameFinal = `${field}_final`;
      const fieldNameExcel = `${field}_excel`;
      result[fieldNameFinal] = getFinalValue(
        item?.[field],
        reference?.[field === 'price' ? 'Price' : field],
      );
      result[fieldNameExcel] = getFinalValue(
        item?.[field],
        reference?.[field === 'price' ? 'Price' : field],
      ).join(',');
    });
  } else {
    const breweryFields = ['raw materials', 'by products'];
    const ccName = (item.cc_description || '').trim().toLowerCase();
    fields.forEach((field) => {
      const fieldNameFinal = prefix ? `${prefix}_${field}_final` : `${field}_final`;
      const fieldNameExcel = prefix ? `${prefix}_${field}_excel` : `${field}_excel`;
      const finalValue = getFinalValue(
        item?.[field],
        !refType
          ? reference?.[field]
          : refType === 'secondary'
          ? breweryFields.includes(ccName)
            ? reference?.[field]
            : 'N/A'
          : breweryFields.includes(ccName)
          ? 'N/A'
          : reference?.[field],
      );

      result[fieldNameFinal] = finalValue;
      result[fieldNameExcel] = finalValue.join(',');
    });
  }

  return result;
};

export const transformCCData = (data, plantInfo) => {
  return data.map((item) => {
    const referencePrimary = item.ref_v.primary[0] || {};
    const referenceSecondary = item.ref_v.secondary[0] || {};
    const fields = ['overall_cost', 'fixed_cost', 'variable_cost'];

    return {
      id: item.id,
      cost_component: item.cost_component,
      cc_description: item.cc_description,
      currency: item.currency,
      comment: item.comment,
      vic_comment: item.vic_comment,
      ...getValueData(
        item,
        referencePrimary,
        fields,
        'cc',
        Object.keys(referenceSecondary).length > 0 ? 'primary' : null,
      ),
      ...getValueData(item, referenceSecondary, fields, 'cc', 'secondary'),
      ...plantInfo[item.pp_id],
    };
  });
};

export const transformBomData = (data, plantInfo) => {
  const fields = ['total_value', 'quantity', 'price', 'un', 'price_unit', 'scrap'];

  const transformEntry = (entry) => {
    const reference = entry.reference_bom?.[0] || {};
    const resource = entry.RequestedSkuCostEstimation?.resource || '';

    return {
      component_id: entry.component_id,
      component_description: entry.component_description,
      component: resource.length > 0 ? resource : entry.component,
      currency: entry.currency,
      component_unit: entry.component_unit,
      un: entry.RequestedSkuCostEstimation?.un,
      comment: entry.comment,
      vic_comment: entry.vic_comment,
      ...getValueData(
        entry?.RequestedSkuCostEstimation,
        reference?.PrimaryRefSkuCostEstimation || {},
        fields,
        'bom',
      ),
      ...plantInfo[entry.pp_id],
    };
  };

  return data.map((item) => {
    // If the item has a material_group, only transform its subRows
    if (item.material_group!== "" && Array.isArray(item.subRows)) {
      return {
        ...item,
        subRows: item.subRows.map((sub) => transformEntry(sub)),
      };
    }

    // Otherwise, transform the item itself
    return transformEntry(item);
  });
};



export const CostValue = ({ cost = '' }) => {
  const isVariance = cost.includes('Var');
  const costValue = cost.split(':')[1];
  return <div className={isVariance && costValue > 0 ? 'red' : ''}>{costValue}</div>;
};

export const CostValueTempAlt = ({ main = {}, refData = {}, field }) => {
  // gracefully handle empty data
  if (!main || !refData || !field) {
    return <div style={{ textAlign: 'center' }}>N/A</div>;
  }

  const mainVal = parseFloat(main[field]).toFixed(3) || 0;
  const refVal = parseFloat(refData[field]).toFixed(3) || 0;
  const variance = (mainVal - refVal).toFixed(3);
  const isVariancePositive = parseFloat(variance) > 0;

  return (
    <Table
      style={{
        tableLayout: 'fixed',
        width: '100%',
        fontSize: '12px',
        borderCollapse: 'collapse',
      }}
    >
      <thead>
        <tr>
          {['Main', 'Ref', 'Var'].map((key) => (
            <th
              key={key}
              style={{
                textAlign: 'center',
                padding: '2px 4px',
                fontWeight: 600,
                borderBottom: '1px solid #ccc',
              }}
            >
              {key}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style={{ textAlign: 'center', padding: '2px 4px' }}>
            {mainVal || 'N/A'}
          </td>
          <td style={{ textAlign: 'center', padding: '2px 4px' }}>
            {refVal || 'N/A'}
          </td>
          <td
            style={{
              textAlign: 'center',
              padding: '2px 4px',
              color: isVariancePositive ? 'red' : 'inherit',
            }}
          >
            {variance}
          </td>
        </tr>
      </tbody>
    </Table>
  );
};


export const CostValueTemp = ({ cost = [] }) => {
  if (!Array.isArray(cost) || cost.length === 0) {
    return <div style={{ textAlign: 'center' }}>N/A</div>;
  }

  const parsed = cost.reduce((acc, item) => {
    const [key, value] = item.split(':').map((v) => v.trim());
    acc[key] = value || 'N/A';
    return acc;
  }, {});

  return (
    <Table
      style={{
        tableLayout: 'fixed',
        width: '100%',
        fontSize: '12px',
        borderCollapse: 'collapse',
      }}
    >
      <thead>
        <tr>
          {['Main', 'Ref', 'Var'].map((key) => (
            <th
              key={key}
              style={{
                textAlign: 'center',
                padding: '2px 4px',
                fontWeight: 500,
                borderBottom: '1px solid #ccc',
              }}
            >
              {key}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr>
          {['Main', 'Ref', 'Var'].map((key) => {
            const value = parsed[key] || 'N/A';
            const isVariance = key === 'Var' && parseFloat(value) > 0;
            return (
              <td
                key={key}
                style={{
                  textAlign: 'center',
                  padding: '2px 4px',
                  color: isVariance ? 'red' : 'inherit',
                }}
              >
                {value}
              </td>
            );
          })}
        </tr>
      </tbody>
    </Table>
  );
};

export const getCommentUrl = (crNumber, type, page) => {
  const vicApprovalCc = `costing-request/step/vic-approval/cc/comment/${crNumber}`;
  const vicApprovalBom = `costing-request/step/vic-approval/bom/comment/${crNumber}`;
  const vicSubmission = `costing-request/step/vic-submission/${crNumber}`;

  return page === 'vicSubmission' ? vicSubmission : type === 'cc' ? vicApprovalCc : vicApprovalBom;
};

export const getData = (data, readOnlyData = []) => {
  if (readOnlyData.length === 0) return data;

  const finalData = [...data];

  readOnlyData.forEach((sku) => {
    finalData.unshift({
      RequestedSkuCostEstimation: {
        ...sku,
      },
      component_id: sku.id,
      component_description: sku.costing_struc,
      component: sku.resource,
      currency: sku.currency,
      component_unit: sku.unit_of_measure,
      pp_id: sku.requested_producing_plant_id,
      read_only_bom: true,
    });
  });

  return finalData;
};

export const ChildCRBanner = ({ crNumber }) => (
  <CrStatusBanner>
    {
      <CustomLink to={`${routes.costingRequests(crNumber).overview}`}>
        Click here to view the child {crNumber}
      </CustomLink>
    }
  </CrStatusBanner>
);
